import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import AddIcon from "@material-ui/icons/Add";
import HolidayRow from "./HolidayRow";
import { i18n } from "../../translate/i18n";

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const useStyles = makeStyles((theme) => ({
  accordion: {
    "&:before": { display: "none" },
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px !important",
    marginBottom: 8,
    overflow: "hidden",
  },
  summaryContent: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  monthLabel: {
    fontWeight: 600,
  },
  activeChip: {
    height: 22,
    fontSize: "0.75rem",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  emptyMonth: {
    padding: "12px 16px",
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  addForm: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
  },
  dayField: {
    width: 72,
  },
  nameField: {
    flex: "1 1 200px",
    minWidth: 160,
  },
}));

/**
 * Lista os 12 meses do ano; em cada mês agrupa feriados nacionais e custom.
 */
const HolidaysAccordion = ({
  holidays,
  onToggleActive,
  onToggleCloseTicket,
  onCreateCustom,
}) => {
  const classes = useStyles();
  const [formByMonth, setFormByMonth] = useState({});
  const [submittingMonth, setSubmittingMonth] = useState(null);

  const getForm = (month) => formByMonth[month] || { day: "", name: "" };

  const setFormField = (month, field, value) => {
    setFormByMonth((prev) => ({
      ...prev,
      [month]: { ...getForm(month), [field]: value },
    }));
  };

  const handleAdd = async (month) => {
    const f = getForm(month);
    const dayNum = parseInt(String(f.day).trim(), 10);
    const name = String(f.name || "").trim();
    if (!name || Number.isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      return;
    }
    setSubmittingMonth(month);
    try {
      await onCreateCustom(month, dayNum, name);
      setFormByMonth((prev) => ({
        ...prev,
        [month]: { day: "", name: "" },
      }));
    } catch {
      // Erro já tratado em useHolidays (toast)
    } finally {
      setSubmittingMonth(null);
    }
  };

  return (
    <Box>
      {MONTH_NAMES.map((monthName, idx) => {
        const month = idx + 1;
        const items = holidays.filter((h) => {
          const m = parseInt(h.date.split("-")[1], 10);
          return m === month;
        });
        const nationalCount = items.filter((h) => h.isNational !== false).length;
        const customCount = items.filter((h) => h.isNational === false).length;
        const activeCount = items.filter((h) => h.active).length;
        const isEmptyMonth = nationalCount === 0 && customCount === 0;
        const f = getForm(month);
        const canSubmit =
          f.name.trim() &&
          !Number.isNaN(parseInt(String(f.day).trim(), 10)) &&
          parseInt(String(f.day).trim(), 10) >= 1 &&
          parseInt(String(f.day).trim(), 10) <= 31;

        return (
          <Accordion key={month} className={classes.accordion} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.summaryContent}>
                <Typography className={classes.monthLabel}>
                  {monthName}
                </Typography>
                {activeCount > 0 && (
                  <Chip
                    label={`${activeCount} ativo${activeCount > 1 ? "s" : ""}`}
                    color="primary"
                    size="small"
                    className={classes.activeChip}
                  />
                )}
                {nationalCount > 0 && (
                  <Chip
                    label={`${nationalCount} nacional${nationalCount > 1 ? "s" : ""}`}
                    size="small"
                    variant="outlined"
                    className={classes.activeChip}
                  />
                )}
                {customCount > 0 && (
                  <Chip
                    label={`${customCount} ${i18n.t("feriados.customPlural")}`}
                    size="small"
                    variant="outlined"
                    className={classes.activeChip}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              {isEmptyMonth && (
                <Typography className={classes.emptyMonth}>
                  {i18n.t("feriados.monthEmptyMessage")}
                </Typography>
              )}
              {items.map((holiday) => (
                <HolidayRow
                  key={holiday.id}
                  holiday={holiday}
                  onToggleActive={onToggleActive}
                  onToggleCloseTicket={onToggleCloseTicket}
                />
              ))}
              <Box className={classes.addForm}>
                <TextField
                  className={classes.dayField}
                  label={i18n.t("feriados.dayLabel")}
                  type="number"
                  size="small"
                  variant="outlined"
                  inputProps={{ min: 1, max: 31 }}
                  value={f.day}
                  onChange={(e) => setFormField(month, "day", e.target.value)}
                  disabled={submittingMonth === month}
                />
                <TextField
                  className={classes.nameField}
                  label={i18n.t("feriados.nameLabel")}
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={f.name}
                  onChange={(e) => setFormField(month, "name", e.target.value)}
                  disabled={submittingMonth === month}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={
                    submittingMonth === month ? (
                      <CircularProgress size={18} />
                    ) : (
                      <AddIcon />
                    )
                  }
                  disabled={!canSubmit || submittingMonth === month}
                  onClick={() => handleAdd(month)}
                >
                  {i18n.t("feriados.addCustom")}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default HolidaysAccordion;
